import React, { useEffect } from 'react'
import { Box, Divider, Stack, Skeleton, Alert, Typography, Button } from '@mui/material'
import { Link } from 'react-router'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import { backgroundColors } from '../../../Common/Color/Color'
import { fontSizes, fontWeights } from '../../../Common/Typography/TypographyStyles'
import OverviewBadges from '../OverviewBadges'
import { setDateFormat, setTimeFormat } from '../../../../utils/dateUtils'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { useLoading } from '../../../../context/LoadingContext'
import { fetchTeamMemberEvent } from '../../../../redux/event/teamMemberEvents/action'
import {
  selectAllEventsSortedByUpcomingForEventSummary,
  selectTeamMemberEventError,
} from '../../../../redux/event/teamMemberEvents/selector'
import { EventWithAdditionalInfo } from '../../../../redux/event/teamMemberEvents/types'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'

const EventSummaryBox = () => {
  const { loading } = useLoading()
  const dispatch = useAppDispatch()
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)
  const eventData = useAppSelector(selectAllEventsSortedByUpcomingForEventSummary)
  const error = useAppSelector(selectTeamMemberEventError)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchTeamMemberEvent({
            teamMemberWorkdayId: teamMemberDetails.workdayId,
            teamMemberEmail: teamMemberDetails.email,
          }),
        )
      } catch (error) {
        console.error('Error fetching team member events:', error)
      }
    }
    fetchData()
  }, [dispatch])

  return (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: backgroundColors.white,
          width: 'auto',
          borderRadius: 2,
          shadows: 1,
          p: 3,
        }}
      >
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
          py={1}
        >
          <Box
            sx={{
              fontSize: { xs: fontSizes.size14, md: fontSizes.size20 },
              fontWeight: fontWeights[500],
            }}
          >
            EVENTS
          </Box>
          <Link to='/events' style={{ textDecoration: 'none' }}>
            <Button size='small' endIcon={<ArrowForwardIosRoundedIcon />}>
              View All
            </Button>
          </Link>
        </Box>
        <Box mt={2}>
          {loading ? (
            // Loading skeleton
            <Stack spacing={2}>
              {[...Array(5)].map((_, index) => (
                <Box key={index}>
                  <Skeleton variant='text' width='100%' />
                  <Skeleton variant='rectangular' height={30} />
                  <Skeleton variant='rectangular' height={20} width='60%' sx={{ marginTop: 1 }} />
                </Box>
              ))}
            </Stack>
          ) : error ? (
            // Error message
            <Alert severity='error'>{error}</Alert>
          ) : eventData.length > 0 ? (
            eventData.slice(0, 5).map((event: EventWithAdditionalInfo) => (
              <Stack key={event.eventDetails.id}>
                <Divider />
                <Box display='flex' flexDirection='column' py={3} gap={4}>
                  <Box
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      fontSize: { xs: fontSizes.size12, md: fontSizes.size16 },
                      fontWeight: fontWeights[700],
                    }}
                  >
                    {event.eventDetails.title}
                  </Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 1, md: 5 }}>
                    <OverviewBadges value={event.eventDetails.category} badgeType='eventCategoryBadge' />
                    <Box display='flex' alignItems='center' gap={1}>
                      <EventRoundedIcon
                        sx={{
                          fontSize: {
                            xs: fontSizes.size12,
                            md: fontSizes.size14,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: {
                            xs: fontSizes.size12,
                            md: fontSizes.size14,
                          },
                        }}
                      >
                        {event.eventDetails.startDate
                          ? setDateFormat(event.eventDetails.startDate)
                          : 'No date available'}
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center' gap={1}>
                      <AccessTimeRoundedIcon
                        sx={{
                          fontSize: {
                            xs: fontSizes.size12,
                            md: fontSizes.size14,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: {
                            xs: fontSizes.size12,
                            md: fontSizes.size14,
                          },
                        }}
                      >
                        {event.eventDetails.startDate
                          ? `${setTimeFormat(event.eventDetails.startDate)} ${Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Shanghai' ? 'Manila' : Intl.DateTimeFormat().resolvedOptions().timeZone} Time`
                          : 'No start time available'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            ))
          ) : (
            <Alert severity='info'>No events available.</Alert>
          )}
        </Box>
      </Box>
    </React.Fragment>
  )
}

export default EventSummaryBox
